/** Schemas dos payloads REST. Contrato único: o servidor valida com estes,
 *  o cliente deriva tipos deles. Ver docs/01-architecture.md. */
import { z } from 'zod';
import { LIMITS, USERNAME_RE } from './channels.js';

export const snowflakeString = z
  .string()
  .regex(/^(?:0|[1-9]\d{0,19})$/, 'snowflake inválido');

// ─── Auth ────────────────────────────────────────────────────────────────────

export const registerSchema = z.object({
  username: z
    .string()
    .min(LIMITS.usernameMin)
    .max(LIMITS.usernameMax)
    .regex(USERNAME_RE, 'só minúsculas, dígitos, _ e .'),
  displayName: z.string().min(1).max(LIMITS.displayNameMax).optional(),
  email: z.string().email().max(254),
  // 12 caracteres em vez de 8: o comprimento é a única propriedade de uma
  // password que resiste a força bruta. Regras de composição só irritam.
  password: z.string().min(12).max(200),
});

export const loginSchema = z.object({
  /** Username ou email — um só campo, como qualquer app moderna. */
  identifier: z.string().min(1).max(254),
  password: z.string().min(1).max(200),
});

export const updateMeSchema = z
  .object({
    displayName: z.string().min(1).max(LIMITS.displayNameMax),
    bio: z.string().max(LIMITS.bioMax),
    locale: z.string().max(10),
  })
  .partial();

// ─── Guilds ──────────────────────────────────────────────────────────────────

export const createGuildSchema = z.object({
  name: z.string().min(LIMITS.guildNameMin).max(LIMITS.guildNameMax),
});

export const updateGuildSchema = z
  .object({
    name: z.string().min(LIMITS.guildNameMin).max(LIMITS.guildNameMax),
    systemChannelId: snowflakeString.nullable(),
  })
  .partial();

// ─── Canais ──────────────────────────────────────────────────────────────────

export const createChannelSchema = z.object({
  name: z
    .string()
    .min(1)
    .max(LIMITS.channelNameMax)
    .regex(/^[^\s#@]+$/, 'sem espaços nem # @'),
  // 0 texto · 1 voz · 2 categoria
  type: z.union([z.literal(0), z.literal(1), z.literal(2)]).default(0),
  topic: z.string().max(LIMITS.channelTopicMax).optional(),
  parentId: snowflakeString.nullish(),
  position: z.number().int().min(0).optional(),
  rateLimitPerUser: z.number().int().min(0).max(21600).optional(),
});

export const updateChannelSchema = createChannelSchema.partial().omit({ type: true });

/**
 * Reordenar canais — a lista inteira de uma vez, não um PATCH por canal.
 *
 * Arrastar um canal mexe na posição de todos os que ficam depois dele, e numa
 * sequência de pedidos independentes qualquer falha a meio deixava a coluna
 * numa ordem que ninguém escolheu. Aqui ou entra tudo ou não entra nada.
 *
 * `parentId` vai junto porque arrastar para dentro de uma categoria é a mesma
 * gesto que mudar de ordem.
 */
export const reorderChannelsSchema = z.object({
  channels: z
    .array(
      z.object({
        id: snowflakeString,
        position: z.number().int().min(0),
        parentId: snowflakeString.nullish(),
      }),
    )
    .min(1)
    .max(LIMITS.channelsPerGuild),
});

export const overwriteSchema = z.object({
  targetId: snowflakeString,
  targetType: z.enum(['role', 'member']),
  /** Bitfields como string decimal — nunca number. Ver docs/04. */
  allow: z.string().regex(/^\d{1,20}$/),
  deny: z.string().regex(/^\d{1,20}$/),
});

// ─── Papéis ──────────────────────────────────────────────────────────────────

export const createRoleSchema = z.object({
  // O produto chama a isto um grupo de permissões; "papel" não aparece em
  // lado nenhum que o utilizador veja, nem sequer num nome por omissão.
  name: z.string().min(1).max(LIMITS.roleNameMax).default('novo grupo'),
  color: z.number().int().min(0).max(0xffffff).default(0),
  permissions: z.string().regex(/^\d{1,20}$/).default('0'),
  hoist: z.boolean().default(false),
  mentionable: z.boolean().default(false),
});

export const updateRoleSchema = createRoleSchema.partial();

export const reorderRolesSchema = z.object({
  roles: z.array(z.object({ id: snowflakeString, position: z.number().int().min(1) })).min(1),
});

export const assignRolesSchema = z.object({
  add: z.array(snowflakeString).max(50).default([]),
  remove: z.array(snowflakeString).max(50).default([]),
});

// ─── Membros ─────────────────────────────────────────────────────────────────

export const updateMemberSchema = z
  .object({
    nick: z.string().max(32).nullable(),
    timeoutUntil: z.string().datetime().nullable(),
  })
  .partial();

/**
 * Moderação de voz. Três permissões distintas num só pedido, verificadas
 * campo a campo: `MUTE_MEMBERS` para `serverMute`, `DEAFEN_MEMBERS` para
 * `serverDeaf`, `MOVE_MEMBERS` para `channelId`.
 *
 * `channelId: null` tira a pessoa da voz — é o "desligar" e também conta
 * como mover.
 */
export const moderateVoiceStateSchema = z
  .object({
    serverMute: z.boolean(),
    serverDeaf: z.boolean(),
    channelId: snowflakeString.nullable(),
  })
  .partial();

export const banSchema = z.object({
  reason: z.string().max(512).optional(),
  deleteMessageSeconds: z.number().int().min(0).max(604800).default(0),
});

// ─── Convites ────────────────────────────────────────────────────────────────

export const createInviteSchema = z.object({
  maxUses: z.number().int().min(0).max(1000).default(0),
  maxAge: z.number().int().min(0).max(604800).default(86400),
  temporary: z.boolean().default(false),
});

// ─── Mensagens ───────────────────────────────────────────────────────────────

export const createMessageSchema = z
  .object({
    content: z.string().max(LIMITS.messageContentMax).default(''),
    /** Idempotência e reconciliação óptimista no cliente. */
    nonce: z.string().max(64).optional(),
    referenceId: snowflakeString.optional(),
    attachmentIds: z.array(snowflakeString).max(LIMITS.attachmentsPerMessage).default([]),
  })
  .refine((m) => m.content.trim().length > 0 || m.attachmentIds.length > 0, {
    message: 'uma mensagem precisa de conteúdo ou de anexos',
  });

export const editMessageSchema = z.object({
  content: z.string().min(1).max(LIMITS.messageContentMax),
});

export const historyQuerySchema = z.object({
  /** Keyset, nunca OFFSET — ver docs/02-data-model.md. */
  before: snowflakeString.optional(),
  after: snowflakeString.optional(),
  around: snowflakeString.optional(),
  limit: z.coerce.number().int().min(1).max(LIMITS.historyPageMax).default(50),
});

export const ackSchema = z.object({ messageId: snowflakeString });

export const paginationSchema = z.object({
  after: snowflakeString.optional(),
  limit: z.coerce.number().int().min(1).max(1000).default(100),
});

export type RegisterBody = z.infer<typeof registerSchema>;
export type LoginBody = z.infer<typeof loginSchema>;
export type CreateMessageBody = z.infer<typeof createMessageSchema>;
export type HistoryQuery = z.infer<typeof historyQuerySchema>;
